/**
 * quest-poi-store — R232q22 Phase 3 Sirus rebuild.
 *
 * Динамические Quest POI («!»/«?»-маркеры) для Minimap и WorldMap, которые
 * вычисляются реактивно из:
 *   • уровня игрока,
 *   • Set-а принятых квестов (`accepted`),
 *   • Set-а завершённых квестов (`completed`),
 *   • статичного каталога `QUESTS` из `data/quests.ts`.
 *
 * NPC-квестодатели агрегируются по `giver`-id (одна точка на NPC даже если
 * у него несколько квестов). Каждой точке присваивается лучший «kind»:
 *   turnin       — есть принятый квест, готовый к сдаче (приоритет 1)
 *   available    — есть НОВЫЙ квест с `minLevel ≤ selfLevel` (приоритет 2)
 *   in-progress  — есть принятый, но ещё не готовый к сдаче (приоритет 3)
 * Если ни одно правило не подошло — NPC пропускается.
 *
 * НЕ делает (deferred):
 *   • Реальный per-zone NPC-quest registry с серверных pack-файлов
 *     `aiwari-server/data/npcs/*.json` — пока используем `QUESTS.giver` /
 *     `giverPos` как единственный источник.
 *   • Server-driven POI sync (RPC `quest-pois-mine`) — POI считается
 *     локально из catalogue + storage Set'ов.
 *   • Анимированный pulse в canvas — это рисует Minimap.tsx своим draw-loop.
 *
 * R232q22 Phase 3: подписки на recompute триггерятся из `app.tsx` через
 * window-события `aiwari-quest-accept` / `aiwari-quest-complete` и change-
 * подписку на `useNet().players[selfId].level`.
 */

import { create } from 'zustand';
import { QUESTS, type Quest } from '../data/quests.js';
import { zoneAt } from '../data/zones.js';

export type QuestPoiKind = 'available' | 'turnin' | 'in-progress';

export interface QuestPoi {
  /** NPC id (Quest.giver). */
  npcId: string;
  /** Локализованное имя NPC (RU). */
  npcName: string;
  /** id зоны, в которой стоит NPC (вычисляется через `zoneAt(x,z)`). */
  zoneId: string;
  /** World-space координаты NPC (для проекции на minimap/worldmap). */
  x: number;
  z: number;
  /** Тип маркера. */
  kind: QuestPoiKind;
  /** id-квестов, которые этот NPC выдаёт / принимает. */
  questIds: string[];
  /** Минимальный требуемый уровень среди questIds (для UX-tooltip). */
  minLevel: number;
}

export interface QuestPoiInput {
  selfLevel: number;
  accepted: Set<string>;
  completed: Set<string>;
}

export interface QuestPoiStore {
  pois: QuestPoi[];
  recompute: (input: QuestPoiInput) => void;
}

/** Группирует QUESTS по `giver` для O(1)-доступа на пересчёте. */
const QUESTS_BY_GIVER: Map<string, Quest[]> = (() => {
  const m = new Map<string, Quest[]>();
  for (const q of QUESTS) {
    const list = m.get(q.giver);
    if (list) list.push(q);
    else m.set(q.giver, [q]);
  }
  return m;
})();

function classifyGiver(
  quests: Quest[],
  input: QuestPoiInput,
): { kind: QuestPoiKind; matched: string[]; minLevel: number } | null {
  const { selfLevel, accepted, completed } = input;

  const turninIds: string[] = [];
  const availableIds: string[] = [];
  const inProgressIds: string[] = [];
  let bestMin = Number.POSITIVE_INFINITY;

  for (const q of quests) {
    if (completed.has(q.id)) continue;

    // Принятый квест — кандидат либо в turnin, либо in-progress.
    if (accepted.has(q.id)) {
      // Без серверного objective-прогресса считаем все принятые квесты
      // готовыми к сдаче только если NPC, к которому они вернутся, — этот
      // же giver. Сейчас нет отдельного поля turnin-NPC, поэтому всегда
      // помечаем как in-progress (NpcQuestMarkers переопределит на
      // turnin когда придёт `aiwari-quest-progress`-событие).
      inProgressIds.push(q.id);
      continue;
    }

    // Новый квест: проверяем prereqs + minLevel.
    if (q.prereqs && q.prereqs.length > 0) {
      const ok = q.prereqs.every((id) => completed.has(id));
      if (!ok) continue;
    }
    if (selfLevel < q.minLevel) continue; // ещё не открыт по уровню
    availableIds.push(q.id);
    if (q.minLevel < bestMin) bestMin = q.minLevel;
  }

  if (turninIds.length > 0) {
    return { kind: 'turnin', matched: turninIds, minLevel: bestMin === Infinity ? 1 : bestMin };
  }
  if (availableIds.length > 0) {
    return { kind: 'available', matched: availableIds, minLevel: bestMin === Infinity ? 1 : bestMin };
  }
  if (inProgressIds.length > 0) {
    return { kind: 'in-progress', matched: inProgressIds, minLevel: bestMin === Infinity ? 1 : bestMin };
  }
  return null;
}

function computePois(input: QuestPoiInput): QuestPoi[] {
  const out: QuestPoi[] = [];
  for (const [giverId, quests] of QUESTS_BY_GIVER.entries()) {
    const cls = classifyGiver(quests, input);
    if (!cls) continue;
    const head = quests[0];
    if (!head) continue;
    const z = zoneAt(head.giverPos.x, head.giverPos.z);
    if (!z) continue;
    out.push({
      npcId: giverId,
      npcName: head.giverName.ru,
      zoneId: z.id,
      x: head.giverPos.x,
      z: head.giverPos.z,
      kind: cls.kind,
      questIds: cls.matched,
      minLevel: cls.minLevel,
    });
  }
  return out;
}

export const useQuestPoi = create<QuestPoiStore>((set) => ({
  pois: [],
  recompute: (input) => set({ pois: computePois(input) }),
}));
